// VASP (Ctrl-Alt v2) status axis — shared by the investor and property-owner
// VASP onboarding surfaces.
//
// Distinct from the Sliceal lifecycle statuses (InvestorStatus in
// investorTypes.ts, PROPERTY_OWNER_STATUS in propertyOwnerStatus.ts): VASP
// status is only ever surfaced in the VASP onboarding sections; every other
// section shows the Sliceal status. The values mirror the Ctrl-Alt v2
// organisation / verification statuses. `failed` is a client-side terminal
// label the drawer surfaces for a rejected registration attempt; `draft` comes
// from the property-owner `ctrlAltStatus` axis
// (propertyOwners/accounts/constants/propertyOwnerStatus.ts CTRL_ALT_STATUS).
export const VASP_USER_STATUS = {
  NOT_SYNCED: 'not_synced',
  DRAFT: 'draft',
  PENDING: 'pending',
  AWAITING_REGISTRATION: 'awaiting_registration',
  ACTIVE: 'active',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  FAILED: 'failed',
} as const

export type VaspUserStatus = (typeof VASP_USER_STATUS)[keyof typeof VASP_USER_STATUS]

// The actionable lanes shown in the VASP onboarding queue: pending submission,
// awaiting external registration, previously rejected, and active (approved).
// Active rows remain in the queue so operators can re-sync their status from
// the kebab — the Ctrl-Alt v2 backend now accepts sync for active registrations.
export const VASP_QUEUE_STATUSES: VaspUserStatus[] = [
  VASP_USER_STATUS.PENDING,
  VASP_USER_STATUS.AWAITING_REGISTRATION,
  VASP_USER_STATUS.ACTIVE,
  VASP_USER_STATUS.REJECTED,
]

// StatusBadge color per VASP status — OCP lookup (coding-principles §9.4)
// keyed by the raw string so an unknown/new server value resolves to neutral.
type VaspStatusColor = 'success' | 'warning' | 'danger' | 'neutral'

export const VASP_STATUS_COLOR_MAP: Record<string, VaspStatusColor> = {
  [VASP_USER_STATUS.ACTIVE]: 'success',
  [VASP_USER_STATUS.APPROVED]: 'success',
  [VASP_USER_STATUS.PENDING]: 'warning',
  [VASP_USER_STATUS.AWAITING_REGISTRATION]: 'warning',
  [VASP_USER_STATUS.DRAFT]: 'warning',
  [VASP_USER_STATUS.REJECTED]: 'danger',
  [VASP_USER_STATUS.FAILED]: 'danger',
  [VASP_USER_STATUS.NOT_SYNCED]: 'neutral',
}

export function vaspStatusColor(status: string | undefined): VaspStatusColor {
  if (!status) return 'neutral'
  return VASP_STATUS_COLOR_MAP[status] ?? 'neutral'
}

// ── VASP queue filter cards ───────────────────────────────────────────────
// The 4 status-filter cards shown above the investor / property-owner VASP
// onboarding queues (All entries · Pending · Active · Rejected). Each card
// maps to the set of `vaspUserStatus` values it represents; clicking a card
// narrows the queue to those statuses. Single source of truth shared by both
// VASP containers and both queue-counts endpoints so buckets never drift.
export type VaspQueueCardKey = 'all' | 'pending' | 'active' | 'rejected'

export const VASP_CARD_STATUS_FILTERS: Record<VaspQueueCardKey, VaspUserStatus[]> = {
  all: VASP_QUEUE_STATUSES,
  pending: [VASP_USER_STATUS.PENDING, VASP_USER_STATUS.AWAITING_REGISTRATION],
  active: [VASP_USER_STATUS.ACTIVE],
  rejected: [VASP_USER_STATUS.REJECTED],
}
